export const ANALYTICS_SECTIONS = [
  { id: 'overzicht', hash: 'overzicht', label: 'Overzicht' },
  { id: 'google', hash: 'google', label: 'Google' },
  { id: 'funnel', hash: 'funnel', label: 'Funnel' },
  { id: 'campagnes', hash: 'campagnes', label: 'Campagnes' },
  { id: 'pipeline', hash: 'pipeline', label: 'Pipeline' },
  { id: 'attributie', hash: 'attributie', label: 'Attributie' },
  { id: 'doelen', hash: 'doelen', label: 'Doelen' },
] as const;

export type AnalyticsSectionId = (typeof ANALYTICS_SECTIONS)[number]['id'];

export const STICKY_NAV_OFFSET_PX = 120;

export type FunnelCounts = {
  new_c: number;
  contacted_c: number;
  replied_c: number;
  meeting_c: number;
  customer_c: number;
  deals_won: number;
};

export type FunnelStep = {
  key: string;
  label: string;
  count: number;
  href?: string;
  pctOfPrevious: number | null;
  pctOfNew: number | null;
};

export function buildFunnelSteps(funnel: FunnelCounts): FunnelStep[] {
  const steps: Omit<FunnelStep, 'pctOfPrevious' | 'pctOfNew'>[] = [
    { key: 'new', label: 'Nieuw', count: funnel.new_c, href: '/app/leads?status=new' },
    {
      key: 'contacted',
      label: 'Contact',
      count: funnel.contacted_c,
      href: '/app/leads?status=contacted',
    },
    {
      key: 'replied',
      label: 'Reactie',
      count: funnel.replied_c,
      href: '/app/leads?status=replied',
    },
    {
      key: 'meeting',
      label: 'Meeting',
      count: funnel.meeting_c,
      href: '/app/leads?status=meeting',
    },
    {
      key: 'customer',
      label: 'Klant',
      count: funnel.customer_c,
      href: '/app/leads?status=customer',
    },
    { key: 'deals_won', label: 'Deals gewonnen', count: funnel.deals_won },
  ];

  const base = funnel.new_c;
  return steps.map((s, i) => {
    const prevCount = i > 0 ? steps[i - 1].count : 0;
    const pctOfPrevious =
      i === 0 ? null : prevCount > 0 ? Math.round((s.count / prevCount) * 1000) / 10 : 0;
    const pctOfNew = base > 0 ? Math.round((s.count / base) * 1000) / 10 : 0;
    return { ...s, pctOfPrevious, pctOfNew };
  });
}

export function funnelConversions(steps: FunnelStep[]): { label: string; pct: number }[] {
  const out: { label: string; pct: number }[] = [];
  for (let i = 1; i < steps.length; i += 1) {
    const prev = steps[i - 1];
    const cur = steps[i];
    const pct = prev.count > 0 ? Math.round((cur.count / prev.count) * 1000) / 10 : 0;
    out.push({ label: `${prev.label} → ${cur.label}`, pct });
  }
  return out;
}

export function largestDropOff(conversions: { label: string; pct: number }[]): string | null {
  if (!conversions.length) return null;
  let min = conversions[0];
  for (const c of conversions) {
    if (c.pct < min.pct) min = c;
  }
  return min.label;
}

export function aggregateWinRate(
  rows: { win_rate: number; won: number; total: number }[],
): number | null {
  let won = 0;
  let total = 0;
  for (const r of rows) {
    won += r.won;
    total += r.total;
  }
  if (total === 0) return null;
  return won / total;
}

export function replyRateFromFunnel(funnel: FunnelCounts): number | null {
  if (funnel.contacted_c === 0) return null;
  return funnel.replied_c / funnel.contacted_c;
}
